import { Loader2, UserMinus } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { RagBadge } from "./RagBadge.js";
import type { RagMember } from "../types.js";

interface RagMemberRowProps {
  member: RagMember;
  onRemove?: (member: RagMember) => void;
  removing?: boolean;
}

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const ROLE_LABELS: Record<RagMember["role"], string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

export function RagMemberRow({ member, onRemove, removing = false }: RagMemberRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-[var(--lm-border-subtle)] bg-white px-3.5 py-2.5 text-[13px] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-slate-900/45 dark:shadow-none">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--avatar-user-bg)] text-[11px] font-semibold text-[var(--avatar-user-fg)] dark:bg-violet-500/30 dark:text-white">
          {initials(member.name)}
        </div>
        <div className="min-w-0">
          <div className="truncate text-[var(--lm-foreground-strong)] dark:text-white">{member.name}</div>
          <div className="truncate text-[11px] text-[var(--lm-muted)] dark:text-slate-400">{member.email}</div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <RagBadge variant={member.role}>{ROLE_LABELS[member.role]}</RagBadge>
        {onRemove ? (
          <IconButton
            type="button"
            size="sm"
            variant="destructive"
            title={`Remove ${member.name}`}
            aria-label={`Remove ${member.name}`}
            disabled={removing}
            onClick={() => onRemove(member)}
          >
            {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />}
          </IconButton>
        ) : null}
      </div>
    </div>
  );
}
